import { get, post, put } from "aws-amplify/api";
import { Alert, Button, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function ScheduleList({ isAuthenticated, user }) {
  const scheduleIdPrefix = "schedule-list";

  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const cityscope = params.cityscope;
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async (cityscopeParam) => {
    const response = await get({
      apiName: "colin",
      path: "/schedules",
      options: {
        queryParams: {
          cityscope: cityscopeParam
        }
      }
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localSchedules = await getData(cityscope);
        setData(localSchedules || []);
        setIsLoading(false);
      } catch (e) {
        alert(e);
      }
      document.title = "Schedule lookup";
    })();
  }, [isAuthenticated, cityscope, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/schedules/new" : `/schedules/${record["schedule_id"]}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      const response = await result.response;
      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
  const handleSave = async ({ row, column, value }) => {
    const recordToSave = { ...row.original, [column.id]: value };
    const appendixValue = String(recordToSave.appendix ?? "").trim();
    const hasIssueDate = !!recordToSave.issue_date;


    // Validation
    if (recordToSave.new_record && !recordToSave.cityscope) return;
    if (appendixValue !== "" && !/^-?\d+$/.test(appendixValue)) {
      setUi({ showAlert: true, errorMessage: "Appendix should be numeric." });
      return;
    }
    if (recordToSave.isSaving) return;

    setUi({ showAlert: false, errorMessage: "" });

    if (recordToSave.new_record && (appendixValue === "" || !hasIssueDate)) {
      return;
    }

    setData(old => old.map((r, index) =>
      index === row.index ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      const isNew = !!recordToSave.new_record;
      const response = await saveRecord(recordToSave);

      if (response?.result === false) {
        setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
        setUi({
          showAlert: true,
          errorMessage: response.error_message || "Failed to save record.",
        });
        return;
      }

      // On update, the API always echoes back id: 1, so keep the existing schedule_id instead.
      const savedScheduleId = isNew ? (response?.id || recordToSave.schedule_id) : recordToSave.schedule_id;

      setData(old => old.map((r, index) =>
        index === row.index
          ? {
            ...r,
            schedule_id: savedScheduleId,
            [column.id]: value,
            modified: false,
            isSaving: false,
            new_record: false,
          }
          : r
      ));
      setUi({ showAlert: false, errorMessage: "" });
    } catch (err) {
      setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
      let errorMessage = "Network Error: Could not save record.";
      try {
        const errBody = await err.response.body.json();
        if (errBody?.message) {
          errorMessage = errBody.message;
        }
      } catch (_) {}
      setUi({ showAlert: true, errorMessage });
    }
  };

  const addRecord = () => {
    const nextAppendix = data.length > 0 ? (Number(data[0].appendix) || 0) + 1 : 1;

    setData(prev => [{
      appendix: nextAppendix,
      issue_date: new Date().toISOString(),
      cityscope: cityscope,
      created_by: user,
      last_edited_by: user,
      new_record: true
    }, ...prev]);
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("schedule_id", {
      id: "schedule_id",
      header: "ID",
      size: 80,
    }),
    columnHelper.accessor("cityscope", {
      id: "cityscope",
      header: "Cityscope",
      size: 150,
    }),
    columnHelper.accessor("issue_date", {
      id: "issue_date",
      header: "Issue Date",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("appendix", {
      id: "appendix",
      header: "Appendix",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      size: 150,
    }),
  ], []);

  const createId = useMemo(
    () => createElementIdFactory(scheduleIdPrefix),
    [scheduleIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Link id={createId("back", "link")} to={{ pathname: `/cityscopes`, }}>
          <Button id={createId("back", "button")} type="button" size="sm" variant="primary"> Back to Cityscopes </Button>
        </Link>{" "}
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="schedule-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.schedule}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                  shouldForceSaveOnBlur: ({ row, column }) => (
                    !!row?.original?.new_record
                    && ["issue_date", "appendix"].includes(column?.id)
                    && !!row?.original?.cityscope
                  ),
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default ScheduleList;